import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { SERVER } from '../hooks/config';
import '../styles/pages/Checkout.scss';

const Checkout = () => {
  const { items, getCartTotal, getTotalShippingCost, clearCart } = useCart();
  const navigate = useNavigate();
  
  // Check for personalized order in localStorage
  const personalizedOrder = JSON.parse(localStorage.getItem('personalizedOrder') || 'null');
  const isPersonalizedOrder = personalizedOrder?.isPersonalized || false;
  const referencePhoto = personalizedOrder?.referencePhoto;
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_city: '',
    customer_zip_code: '',
    order_notes: isPersonalizedOrder ? personalizedOrder.orderNotes : ''
  });
  
  // Use personalized items if available, otherwise use cart items with size and shipping
  const orderItems = isPersonalizedOrder ? 
    personalizedOrder.items : 
    items.map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      product_price: item.product_price,
      quantity: item.quantity,
      selected_size: item.selectedSize, // Add selected size
      shipping_cost: item.shipping_cost, // Add shipping cost
      total_price: item.product_price * item.quantity,
      customizations: item.customizations
    }));
  
  const orderSubtotal = isPersonalizedOrder ? personalizedOrder.subtotal : getCartTotal();
  const shippingCost = isPersonalizedOrder ? 7.00 : getTotalShippingCost(); // Dynamic shipping cost
  
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create FormData to handle file upload
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Add order data
      formDataToSend.append('subtotal', orderSubtotal);
      formDataToSend.append('shipping_cost', shippingCost);
      formDataToSend.append('items', JSON.stringify(orderItems));
      formDataToSend.append('is_personalized', isPersonalizedOrder.toString());
      
      // Add reference photo if it exists
      if (referencePhoto) {
        // Convert base64 to blob if it's a base64 string
        if (referencePhoto.startsWith('data:image')) {
          const response = await fetch(referencePhoto);
          const blob = await response.blob();
          formDataToSend.append('reference_photo', blob, 'reference-photo.jpg');
        } else if (referencePhoto instanceof File) {
          formDataToSend.append('reference_photo', referencePhoto);
        }
      }

      const response = await axios.post(`${SERVER}/orders`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Clear both cart and personalized order data
      clearCart();
      localStorage.removeItem('personalizedOrder');
      
      navigate('/checkout/success', { 
        state: { orderNumber: response.data.order_number } 
      });
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Erreur lors de la commande. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1>Finaliser la commande</h1>
        
        <div className="checkout-content">
          <form onSubmit={handleSubmit} className="checkout-form">
            <h2>Informations de livraison</h2>
            
            {isPersonalizedOrder && (
              <div className="personalized-notice">
                <p><strong>Commande personnalisée</strong> - Notre équipe vous contactera pour finaliser les détails et le prix.</p>
              </div>
            )}
            
            <div className="form-row">
              <div className="form-group">
                <label>Nom complet *</label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Téléphone *</label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Adresse *</label>
              <input
                type="text"
                name="customer_address"
                value={formData.customer_address}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ville *</label>
                <input
                  type="text"
                  name="customer_city"
                  value={formData.customer_city}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Code postal *</label>
                <input
                  type="text"
                  name="customer_zip_code"
                  value={formData.customer_zip_code}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notes de commande (optionnel)</label>
              <textarea
                name="order_notes"
                value={formData.order_notes}
                onChange={handleInputChange}
                rows="3"
              />
            </div>

            <div className="payment-method">
              <h3>Méthode de paiement</h3>
              <div className="payment-option">
                <input
                  type="radio"
                  id="cash_on_delivery"
                  name="payment_method"
                  value="cash_on_delivery"
                  defaultChecked
                />
                <label htmlFor="cash_on_delivery">
                  Paiement à la livraison
                </label>
              </div>
            </div>

            <button type="submit" disabled={loading} className="submit-order-btn">
              {loading ? 'Traitement...' : 'Confirmer la commande'}
            </button>
          </form>

          <div className="order-summary">
            <h2>Résumé de la commande</h2>
            <div className="order-items">
              {orderItems.map((item, index) => (
                <div key={index} className="order-item">
                  <div className="item-info">
                    <h4>{item.product_name}</h4>
                    <p>Quantité: {item.quantity}</p>
                    {item.selected_size && (
                      <p>Taille: {item.selected_size}</p>
                    )}
                    {item.product_price > 0 ? (
                      <p className="price">{item.product_price} DT x {item.quantity}</p>
                    ) : (
                      <p className="price">Prix à déterminer</p>
                    )}
                    {item.customizations && (
                      <div className="customizations">
                        <p><small>Personnalisation: {item.customizations.modele} - {item.customizations.matiere}</small></p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="summary-totals">
              <div className="summary-item">
                <span>Sous-total:</span>
                <span>{orderSubtotal > 0 ? `${orderSubtotal.toFixed(2)} DT` : 'À déterminer'}</span>
              </div>
              <div className="summary-item">
                <span>Livraison:</span>
                <span>{shippingCost} DT</span>
              </div>
              <div className="summary-item total">
                <span>Total:</span>
                <span>{orderSubtotal > 0 ? `${(orderSubtotal + shippingCost).toFixed(2)} DT` : 'À déterminer'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;